import { FC, useCallback } from 'react';

import {
  Document,
  transcriptorApi,
} from '@/shared/api/transcriptor/transcriptorApi';
import { useAppDispatch } from '@/shared/hooks';

import {
  TranscriptorConsultation,
  transcriptorModel,
} from '@/entities/transcriptor';

import { ConsultationNode, dentalConsultationMessages } from '../../config';
import {
  convertNodesToDocumentUpdate,
  isDocumentFailedWithoutContent,
  isDocumentGenerating,
  parseFollowUpData,
} from '../../lib';
import { ConsultationEmptyState } from '../ConsultationEmptyState/ConsultationEmptyState';
import { ConsultationWidget } from '../ConsultationWidget/ConsultationWidget';

type FollowUpWidgetProps = {
  consultation?: TranscriptorConsultation | null;
  followUp?: Document | null;
  onRegenerate?: () => void;
};

export const FollowUpWidget: FC<FollowUpWidgetProps> = ({
  consultation,
  followUp,
  onRegenerate,
}) => {
  const dispatch = useAppDispatch();
  const consultationId = consultation?.id;
  const patientId = consultation?.patientId;
  const status = consultation?.status;
  const followUpId = followUp?.id;
  const documentStatus = followUp?.status;

  const handleSave = useCallback(
    async (editedData: ConsultationNode[]) => {
      if (!consultationId) {
        throw new Error('Consultation ID is missing');
      }

      if (!followUpId) {
        throw new Error('Follow-up document is not yet generated');
      }

      const updateRequest = convertNodesToDocumentUpdate(editedData);
      await transcriptorApi.updateConsultationDocument(
        consultationId,
        followUpId,
        updateRequest,
      );

      dispatch(
        transcriptorModel.thunks.fetchConsultationById({
          consultationId,
          patientId,
        }),
      );
    },
    [consultationId, followUpId, patientId],
  );

  if (!followUp) {
    return (
      <ConsultationEmptyState
        status={status}
        noDataMessage={dentalConsultationMessages.noFollowUpAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  if (
    documentStatus === 'skipped' ||
    isDocumentGenerating(followUp) ||
    isDocumentFailedWithoutContent(followUp)
  ) {
    return (
      <ConsultationEmptyState
        status={status}
        documentStatus={documentStatus}
        documentError={followUp?.error}
        noDataMessage={dentalConsultationMessages.noFollowUpAvailable}
        onRegenerate={onRegenerate}
      />
    );
  }

  const data = parseFollowUpData(followUp);

  return (
    <ConsultationWidget
      data={data}
      showActions
      widgetType="followUp"
      transcriptChunks={consultation?.transcriptChunks}
      onSave={handleSave}
    />
  );
};
